import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { ChipColour } from '../../../../chip/chip.component';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DRIVER_THRESHOLDS, getDriverKeys } from './driver-thresholds';

export const DRIVER_SIGN_KEYS = {
  tearFilm: {
    lipid: ['LIPID_LAYER', 'MGE_EXPRESSIBILITY', 'MGE_SECRETION'],
    aqueous: ['TEAR_MENISCUS_HEIGHT', 'SCHIRMERS_TEST', 'PHENOL_RED', 'SMTUBE'],
    mucinGlycocalyx: ['CORNEAL_STAINING_EXTENT']
  },
  eyelidAnomaly: {
    blinkMechanism: ['PARTIAL_BLINKING_FREQUENCY', 'LAGOPHTHALMUS'],
    lidMargin: {
      anteriorBlepharitis: ['COLLARETTES'],
      meibography: ['MEIBOGRAPHY_UPPER', 'MEIBOGRAPHY_LOWER'],
      glandPlugging: ['MG_ORIFICE_CAPPED_GLANDS'],
      telangiectasia: ['EYELID_TELANGIECTASIA'],
      glandExpressibility: ['MGE_EXPRESSIBILITY', 'MGE_SECRETION'],
      keratinization: ['KERATINIZATION'],
      ocularRosacea: ['EYELID_TELANGIECTASIA']
    }
  },
  ocularSurface: {
    anatomicalAlignment: ['EYE_MARKUP', 'CONJUNCTIVAL_CHALASIS'],
    neuralDysfunction: ['CORNEAL_SENSATION'],
    cellDamageDisruption: ['CORNEAL_STAINING_EXTENT', 'LWE'],
    inflammationOxidativeStress: ['BULBAR_REDNESS', 'MMP9', 'LID_REDNESS']
  }
};

export type DriverSubKey =
  | 'lipid'
  | 'aqueous'
  | 'mucinGlycocalyx'
  | 'blinkMechanism'
  | 'lidMargin'
  | 'anatomicalAlignment'
  | 'neuralDysfunction'
  | 'cellDamageDisruption'
  | 'inflammationOxidativeStress';

export interface DriverSubMeta {
  key: DriverSubKey;
  controlKey: string;
  label: string;
  description: string;
  category: 'tearFilm' | 'eyelidAnomaly' | 'ocularSurface';
}

export const DRIVER_SUB_META: DriverSubMeta[] = [
  {
    key: 'lipid',
    controlKey: 'dewsDriverLipid',
    label: 'Lipid',
    description: 'Meibomian gland dysfunction indicators',
    category: 'tearFilm'
  },
  {
    key: 'aqueous',
    controlKey: 'dewsDriverAqueous',
    label: 'Aqueous',
    description: 'Tear volume and production indicators',
    category: 'tearFilm'
  },
  {
    key: 'mucinGlycocalyx',
    controlKey: 'dewsDriverMucinGlycocalyx',
    label: 'Mucin / Glycocalyx',
    description: 'Conjunctival epithelium mucin and glycocalyx',
    category: 'tearFilm'
  },
  {
    key: 'blinkMechanism',
    controlKey: 'dewsDriverBlinkMechanism',
    label: 'Blink Mechanism',
    description: 'Blink rate and completeness indicators',
    category: 'eyelidAnomaly'
  },
  {
    key: 'lidMargin',
    controlKey: 'dewsDriverLidMargin',
    label: 'Lid Margin',
    description: 'Lid margin and meibomian gland indicators',
    category: 'eyelidAnomaly'
  },
  {
    key: 'anatomicalAlignment',
    controlKey: 'dewsDriverAnatomicalAlignment',
    label: 'Anatomical Alignment',
    description: '',
    category: 'ocularSurface'
  },
  {
    key: 'neuralDysfunction',
    controlKey: 'dewsDriverNeuralDysfunction',
    label: 'Neural Dysfunction',
    description: 'Corneal sensation abnormalities',
    category: 'ocularSurface'
  },
  {
    key: 'cellDamageDisruption',
    controlKey: 'dewsDriverCellDamage',
    label: 'Cell Damage / Disruption',
    description: 'Ocular surface cell damage',
    category: 'ocularSurface'
  },
  {
    key: 'inflammationOxidativeStress',
    controlKey: 'dewsDriverInflammation',
    label: 'Inflammation / Oxidative Stress',
    description: 'Primary inflammation and oxidative stress',
    category: 'ocularSurface'
  }
];

export function getIdentifiedDrivers(controls: { [key: string]: AbstractControl }): string[] {
  return DRIVER_SUB_META.filter(d => {
    const c = controls[d.controlKey];
    return c && c.value === true;
  }).map(d => d.label);
}

@Component({
  selector: 'csi-dews-iii-drivers',
  templateUrl: 'dews-iii-drivers.component.html',
  styleUrls: ['dews-iii-drivers.component.scss']
})
export class DewsIIIDriversComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;
  @Input() assessment: any;
  @Input() onQuestionnaireLinked$: Observable<any>;

  driverSignKeys = DRIVER_SIGN_KEYS;

  // Two-panel navigation state
  readonly driverSubMeta: DriverSubMeta[] = DRIVER_SUB_META;
  selectedDriver: DriverSubKey = 'lipid';
  readonly categoryKeys: Array<'tearFilm' | 'eyelidAnomaly' | 'ocularSurface'> = [
    'tearFilm',
    'eyelidAnomaly',
    'ocularSurface'
  ];
  readonly categoryLabels: { [key: string]: string } = {
    tearFilm: 'Tear Film Drivers',
    eyelidAnomaly: 'Eyelid Anomaly Drivers',
    ocularSurface: 'Ocular Surface Drivers'
  };

  // Pre-computed maps (no function calls in template)
  driversByCategory: { [key: string]: DriverSubMeta[] } = {};
  driverMetaMap: { [key: string]: DriverSubMeta } = {};
  driverChipTexts: { [controlKey: string]: string } = {};
  driverChipColours: { [controlKey: string]: ChipColour } = {};

  dedDiagnosed = false;
  driverIdentified: { [key: string]: boolean } = {};

  // Sign flagging state
  signFlags: { [contextKey: string]: boolean } = {};
  signFlagDescriptions: { [contextKey: string]: string } = {};
  signHasData: { [contextKey: string]: boolean } = {};

  // Track user manual overrides (unchecked after auto-identify)
  private userOverrides: { [driverKey: string]: boolean } = {};
  private previousAutoIdentifyState: { [driverKey: string]: boolean } = {};
  private subscribedSignControlKeys: { [key: string]: boolean } = {};

  constructor(public schemaService: SchemaService) {}

  ngOnInit() {
    // Build static lookup maps
    this.categoryKeys.forEach(cat => {
      this.driversByCategory[cat] = DRIVER_SUB_META.filter(m => m.category === cat);
    });
    DRIVER_SUB_META.forEach(meta => {
      this.driverMetaMap[meta.key] = meta;
    });

    this.ensureControls();
    this.updateDriverState();

    // Use setTimeout to allow symptom controls to register first (same pattern as homeostasis markers)
    setTimeout(() => {
      this.evaluateAndAutoIdentify();
      this.subscribeToSignChanges();
    });

    // Re-evaluate when questionnaires are linked (controls get new values)
    if (this.onQuestionnaireLinked$) {
      this.onQuestionnaireLinked$.pipe(untilDestroyed(this)).subscribe(() => {
        this.evaluateAndAutoIdentify();
      });
    }

    // React to clinician override and diagnosis changes
    const overrideControl = this.formGroup.controls['dewsClinicianOverride'];
    if (overrideControl) {
      overrideControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.updateDriverState();
        setTimeout(() => {
          this.evaluateAndAutoIdentify();
          this.subscribeToSignChanges();
        });
      });
    }

    const diagnosedControl = this.formGroup.controls['dewsDedDiagnosed'];
    if (diagnosedControl) {
      diagnosedControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.updateDriverState();
        setTimeout(() => {
          this.evaluateAndAutoIdentify();
          this.subscribeToSignChanges();
        });
      });
    }
  }

  ngOnDestroy() {}

  selectDriver(key: DriverSubKey): void {
    this.selectedDriver = key;
  }

  isSignFlagged(driverContext: string, signKey: string): boolean {
    return this.signFlags[driverContext + ':' + signKey] === true;
  }

  getSignFlagDescription(driverContext: string, signKey: string): string {
    return this.signFlagDescriptions[driverContext + ':' + signKey] || '';
  }

  toggleDriver(controlKey: string) {
    const control = this.formGroup.controls[controlKey];
    if (control) {
      const wasChecked = control.value === true;
      control.setValue(!control.value);
      if (wasChecked) {
        this.userOverrides[controlKey] = true;
      } else {
        delete this.userOverrides[controlKey];
      }
      this.updateDriverState();
    }
  }

  private ensureControls() {
    const driverControls = [
      'dewsDriverLipid',
      'dewsDriverAqueous',
      'dewsDriverMucinGlycocalyx',
      'dewsDriverBlinkMechanism',
      'dewsDriverLidMargin',
      'dewsDriverAnatomicalAlignment',
      'dewsDriverNeuralDysfunction',
      'dewsDriverCellDamage',
      'dewsDriverInflammation',
      'dewsDriverNotes'
    ];

    driverControls.forEach(key => {
      if (!this.formGroup.controls[key]) {
        this.formGroup.addControl(
          key,
          new FormControl({ value: null, disabled: this.formGroup.disabled })
        );
      }
    });
  }

  private subscribeToSignChanges() {
    const seen: { [key: string]: boolean } = {};

    DRIVER_THRESHOLDS.forEach(t => {
      if (!t.canAutoFlag || seen[t.signKey]) {
        return;
      }
      seen[t.signKey] = true;

      const controlKeys = [
        'RIGHT_' + t.signKey,
        'LEFT_' + t.signKey,
        'RIGHT_' + t.signKey + '_METHOD',
        'LEFT_' + t.signKey + '_METHOD'
      ];

      controlKeys.forEach(controlKey => {
        if (this.subscribedSignControlKeys[controlKey]) {
          return;
        }
        const ctrl = this.formGroup.controls[controlKey];
        if (ctrl) {
          ctrl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.evaluateAndAutoIdentify());
          this.subscribedSignControlKeys[controlKey] = true;
        }
      });
    });
  }

  private evaluateAndAutoIdentify() {
    this.evaluateSignFlags();
    this.autoIdentifyDrivers();
    this.updateDriverState();
  }

  private evaluateSignFlags() {
    const controls = this.formGroup.controls;

    DRIVER_THRESHOLDS.forEach(threshold => {
      const contextKey = threshold.driverContext + ':' + threshold.signKey;

      if (!threshold.canAutoFlag) {
        this.signFlags[contextKey] = false;
        this.signHasData[contextKey] = false;
        this.signFlagDescriptions[contextKey] = '';
        return;
      }

      const hasData = threshold.hasData(controls);
      const flagged = hasData && threshold.evaluate(controls);

      this.signHasData[contextKey] = hasData;
      this.signFlags[contextKey] = flagged;
      this.signFlagDescriptions[contextKey] = flagged ? threshold.description : '';
    });
  }

  private autoIdentifyDrivers() {
    const controls = this.formGroup.controls;
    const driverKeys = getDriverKeys();
    let changed = false;

    driverKeys.forEach(driverKey => {
      const thresholds = DRIVER_THRESHOLDS.filter(t => {
        return t.driverKey === driverKey && t.canAutoFlag;
      });

      if (thresholds.length === 0) {
        return;
      }

      let filledCount = 0;
      let anyFilledMeetsThreshold = false;

      thresholds.forEach(t => {
        const contextKey = t.driverContext + ':' + t.signKey;
        if (this.signHasData[contextKey]) {
          filledCount++;
          if (this.signFlags[contextKey]) {
            anyFilledMeetsThreshold = true;
          }
        }
      });

      const shouldAutoIdentify = filledCount > 0 && anyFilledMeetsThreshold;

      // If auto-identify criteria are no longer met, clear the user override
      if (!shouldAutoIdentify) {
        delete this.userOverrides[driverKey];
      }

      // Only auto-set if no user override
      if (shouldAutoIdentify && !this.userOverrides[driverKey]) {
        const ctrl = controls[driverKey];
        if (ctrl && ctrl.value !== true) {
          ctrl.setValue(true, { emitEvent: false });
          changed = true;
        }
      } else if (!shouldAutoIdentify) {
        // If previously auto-identified and no longer meets criteria, uncheck
        if (this.previousAutoIdentifyState[driverKey] && !this.userOverrides[driverKey]) {
          const ctrl = controls[driverKey];
          if (ctrl && ctrl.value === true) {
            ctrl.setValue(false, { emitEvent: false });
            changed = true;
          }
        }
      }

      this.previousAutoIdentifyState[driverKey] = shouldAutoIdentify;
    });

    if (changed) {
      this.formGroup.updateValueAndValidity();
    }
  }

  private updateDriverState() {
    const controls = this.formGroup.controls;
    const autoDiagnosed =
      controls['dewsDedDiagnosed'] && controls['dewsDedDiagnosed'].value === true;
    const overrideActive =
      controls['dewsClinicianOverride'] && controls['dewsClinicianOverride'].value === true;

    // Clinician override flips the effective diagnosis
    this.dedDiagnosed = overrideActive ? !autoDiagnosed : autoDiagnosed;

    getDriverKeys().forEach(key => {
      this.driverIdentified[key] = controls[key] && controls[key].value === true;
    });

    // Update chip display maps
    DRIVER_SUB_META.forEach(meta => {
      const identified = this.driverIdentified[meta.controlKey];
      this.driverChipTexts[meta.controlKey] = identified ? 'Identified' : 'Not Identified';
      this.driverChipColours[meta.controlKey] = identified ? 'orange' : 'grey-no-outline';
    });
  }
}
